import { F, type Tile } from '../level/types';
import type { World } from './world';

export const PHYS = {
  w: 12,
  h: 18,
  gUp: 1750,
  gCut: 4200,
  gDown: 2500,
  jumpV: 520, // ≈77px apex: one line pitch (60) plus clearance
  springV: 735, // ≈150px super jump from bold words
  bounceV: 610,
  maxRun: 215,
  accelGround: 2100,
  turnGround: 2900,
  decelGround: 2500,
  accelAir: 1500,
  decelAir: 700,
  iceMax: 255,
  iceAccel: 560,
  iceDecel: 130,
  maxFall: 720,
  coyote: 0.1,
  buffer: 0.12,
  climbUp: 175,
  climbDown: 300,
  climbSide: 110,
  corner: 7,
} as const;

export interface PlayerInput {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  jumpPressed: boolean;
}

export type PlayerEvent = 'jump' | 'spring' | 'land' | 'bonk' | 'hazard' | 'climb';

const approach = (v: number, target: number, delta: number) =>
  v < target ? Math.min(v + delta, target) : Math.max(v - delta, target);

export class Player {
  x: number;
  y: number;
  vx = 0;
  vy = 0;
  readonly w = PHYS.w;
  readonly h = PHYS.h;
  onGround = false;
  /** Tiles currently stood on (a player can straddle two words). */
  standing: Tile[] = [];
  coyote = 0;
  buffer = 0;
  facing = 1;
  climbing = false;
  dropTimer = 0;
  invuln = 0;
  /** Seconds since last landing / jump, for animation. */
  airTime = 0;
  runPhase = 0;
  squash = 0;
  private lastGround: Tile | null = null;
  private scratch: Tile[] = [];
  private pre = new Set<number>();

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  get cx(): number {
    return this.x + this.w / 2;
  }

  /** The stood-on tile under the player's center (or the widest overlap). */
  get ground(): Tile | null {
    if (!this.standing.length) return null;
    const c = this.cx;
    let best = this.standing[0];
    let bestOverlap = -1;
    for (const t of this.standing) {
      if (c >= t.x && c <= t.x + t.w) return t;
      const o = Math.min(this.x + this.w, t.x + t.w) - Math.max(this.x, t.x);
      if (o > bestOverlap) {
        bestOverlap = o;
        best = t;
      }
    }
    return best;
  }

  /** A link or door the player is standing on, preferring the center tile. */
  actionTile(): Tile | null {
    const g = this.ground;
    if (g && (g.flags & F.LINK || g.door)) return g;
    for (const t of this.standing) {
      const o = Math.min(this.x + this.w, t.x + t.w) - Math.max(this.x, t.x);
      if (o >= 4 && (t.flags & F.LINK || t.door)) return t;
    }
    return null;
  }

  onLadder(world: World): boolean {
    const l = world.level.ladder;
    const c = this.cx;
    return c >= l.x && c <= l.x + l.w && this.y + this.h > l.y0 && this.y < l.y1;
  }

  teleport(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.climbing = false;
    this.standing = [];
    this.onGround = false;
  }

  step(dt: number, inp: PlayerInput, world: World, emit: (e: PlayerEvent, t?: Tile) => void): void {
    if (this.dropTimer > 0) this.dropTimer -= dt;
    if (this.invuln > 0) this.invuln -= dt;
    this.buffer = inp.jumpPressed ? PHYS.buffer : Math.max(0, this.buffer - dt);
    const dir = (inp.right ? 1 : 0) - (inp.left ? 1 : 0);
    if (dir) this.facing = dir;

    // Tiles we already overlap (e.g. a timed block that re-appeared inside us) are ignored this step.
    this.pre.clear();
    for (const t of world.query(this.x, this.y, this.x + this.w, this.y + this.h, this.scratch))
      if (world.solid(t)) this.pre.add(t.id);

    const ladder = this.onLadder(world);
    if (!this.climbing && ladder && (inp.up || (inp.down && !this.actionTileUnder()))) {
      if (inp.up || inp.down) {
        this.climbing = true;
        this.x = world.level.ladder.x + world.level.ladder.w / 2 - this.w / 2;
        this.vx = 0;
        if (inp.down) this.dropTimer = 0.2;
        emit('climb');
      }
    }

    if (this.climbing) {
      if (!ladder) this.climbing = false;
      else {
        this.vy = inp.up ? -PHYS.climbUp : inp.down ? PHYS.climbDown : 0;
        this.vx = dir * PHYS.climbSide;
        if (this.buffer > 0) {
          this.climbing = false;
          this.buffer = 0;
          this.vy = -PHYS.jumpV * 0.85;
          this.vx = dir * PHYS.maxRun * 0.6;
          emit('jump');
        }
      }
    }

    if (!this.climbing) {
      const g = this.onGround ? this.ground : null;
      const ice = !!g && (g.flags & F.ICE) !== 0;
      const max = ice ? PHYS.iceMax : PHYS.maxRun;
      const target = dir * max;
      let a: number;
      if (this.onGround) {
        if (ice) a = dir ? PHYS.iceAccel : PHYS.iceDecel;
        else if (!dir) a = PHYS.decelGround;
        else a = this.vx !== 0 && Math.sign(this.vx) !== dir ? PHYS.turnGround : PHYS.accelGround;
      } else a = dir ? PHYS.accelAir : PHYS.decelAir;
      this.vx = approach(this.vx, target, a * dt);

      if (this.buffer > 0 && (this.onGround || this.coyote > 0)) {
        const from = this.onGround ? g : this.lastGround;
        const spring = !!from && (from.flags & F.SPRING) !== 0;
        this.vy = -(spring ? PHYS.springV : PHYS.jumpV);
        this.buffer = 0;
        this.coyote = 0;
        this.onGround = false;
        this.standing = [];
        this.airTime = 0;
        this.squash = -0.35;
        emit(spring ? 'spring' : 'jump', from ?? undefined);
      }

      const grav = this.vy < 0 ? (inp.jump ? PHYS.gUp : PHYS.gCut) : PHYS.gDown;
      this.vy = Math.min(this.vy + grav * dt, PHYS.maxFall);
    }

    this.moveX(dt, world);
    this.moveY(dt, inp, world, emit);

    // Red links bite.
    if (this.invuln <= 0)
      for (const t of world.query(this.x - 1, this.y - 1, this.x + this.w + 1, this.y + this.h + 1, this.scratch))
        if (t.flags & F.HAZARD && world.present(t)) {
          emit('hazard', t);
          break;
        }

    if (this.onGround) {
      this.coyote = PHYS.coyote;
      this.lastGround = this.ground;
      this.runPhase += Math.abs(this.vx) * dt * 0.09;
    } else {
      this.coyote = Math.max(0, this.coyote - dt);
      this.airTime += dt;
    }
    this.squash = approach(this.squash, 0, dt * 3);
  }

  private actionTileUnder(): boolean {
    return this.onGround && !!this.actionTile();
  }

  private moveX(dt: number, world: World): void {
    const dx = this.vx * dt;
    if (!dx) return;
    this.x += dx;
    for (const t of world.query(this.x, this.y, this.x + this.w, this.y + this.h, this.scratch)) {
      if (!world.solid(t) || this.pre.has(t.id)) continue;
      if (!(this.x < t.x + t.w && this.x + this.w > t.x && this.y < t.y + t.h && this.y + this.h > t.y)) continue;
      if (dx > 0) this.x = Math.min(this.x, t.x - this.w);
      else this.x = Math.max(this.x, t.x + t.w);
      this.vx = 0;
    }
  }

  private moveY(dt: number, inp: PlayerInput, world: World, emit: (e: PlayerEvent, t?: Tile) => void): void {
    const prevBottom = this.y + this.h;
    const dy = this.vy * dt;
    this.y += dy;
    const wasGround = this.onGround;
    const impact = this.vy;
    this.onGround = false;
    this.standing = [];
    let landedY = Infinity;
    for (const t of world.query(this.x, this.y, this.x + this.w, this.y + this.h, [])) {
      if (this.pre.has(t.id)) continue;
      if (!(this.x < t.x + t.w && this.x + this.w > t.x && this.y < t.y + t.h && this.y + this.h > t.y)) continue;
      if (world.solid(t)) {
        if (dy > 0) {
          if (t.y < landedY) landedY = t.y;
        } else if (dy < 0) {
          // Corner correction: slip past ceiling corners we barely clip.
          const pushLeft = this.x + this.w - t.x;
          const pushRight = t.x + t.w - this.x;
          if (pushLeft <= PHYS.corner && !world.solidRect(this.x - pushLeft, this.y, this.w, this.h, this.pre)) {
            this.x -= pushLeft;
            continue;
          }
          if (pushRight <= PHYS.corner && !world.solidRect(this.x + pushRight, this.y, this.w, this.h, this.pre)) {
            this.x += pushRight;
            continue;
          }
          this.y = t.y + t.h;
          if (this.vy < -60) emit('bonk', t);
          this.vy = 0;
        }
      } else if (
        world.oneway(t) &&
        dy > 0 &&
        prevBottom <= t.y + 0.5 &&
        this.dropTimer <= 0 &&
        !this.climbing
      ) {
        if (t.y < landedY) landedY = t.y;
      }
    }
    if (landedY !== Infinity) {
      this.y = landedY - this.h;
      this.vy = 0;
      this.onGround = true;
      for (const t of world.query(this.x, landedY - 1, this.x + this.w, landedY + 1, []))
        if (Math.abs(t.y - landedY) < 0.5 && (world.solid(t) || world.oneway(t))) this.standing.push(t);
      if (this.climbing && !inp.up) this.climbing = false;
      const g = this.ground;
      if (!wasGround) {
        this.airTime = 0;
        if (g && g.flags & F.SPRING && impact > 240 && !inp.down) {
          this.vy = -(inp.jump ? PHYS.springV : PHYS.bounceV);
          this.onGround = false;
          this.standing = [];
          this.squash = -0.4;
          emit('spring', g);
          return;
        }
        if (impact > 200) {
          this.squash = Math.min(0.5, impact / 1400);
          emit('land', g ?? undefined);
        }
      }
    }
  }
}
